import { Avatar, Box, Button, Chip, Paper, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import { formatDate, STATUS } from "./constants";

// Matches PatientTable's local palette exactly, so this grid looks like the
// same design system rather than an independent color scheme. STATUS colors
// (pending/approved/rejected) still come from constants.js since those are
// semantic, not brand color.
const palette = {
  indigo: "#4B49AC",
  indigoDark: "#37357D",
  indigoSoft: "#EEEDFB",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
};

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

export default function CarryForwardTable({ rows, loading, searching, onDecide }) {
  const columns = [
    {
      field: "avatar",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: 12.5,
              fontWeight: 700,
              backgroundColor: palette.indigoSoft,
              color: palette.indigo,
            }}
          >
            {getInitials(row.patient_name)}
          </Avatar>
        </Box>
      ),
    },
    {
      field: "patient_name",
      headerName: "Patient",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: palette.ink }}>
            {value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "service_name",
      headerName: "Service",
      width: 120,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: palette.indigo,
            backgroundColor: palette.indigoSoft,
          }}
        />
      ),
    },
    {
      field: "original_session_date",
      headerName: "Missed Date",
      width: 140,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "notes",
      headerName: "Reason",
      flex: 1,
      minWidth: 180,
      renderCell: ({ value }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography sx={{ fontSize: 12, color: palette.slate }}>{value || "\u2014"}</Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: ({ value }) => {
        const status = STATUS[value] || STATUS.PENDING;
        return (
          <Chip
            size="small"
            label={status.label}
            sx={{ fontSize: 11, fontWeight: 700, color: status.color, backgroundColor: status.background }}
          />
        );
      },
    },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography sx={{ fontSize: 12, color: palette.slate }}>{value || "\u2014"}</Typography>
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      sortable: false,
      renderCell: ({ row }) =>
        row.status !== "PENDING" ? (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ fontSize: 12, color: palette.slate }}>Decided</Typography>
          </Box>
        ) : (
          <Button
            size="small"
            onClick={() => onDecide(row)}
            startIcon={<GavelRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 700,
              color: palette.indigo,
              backgroundColor: palette.indigoSoft,
              borderRadius: "8px",
              "&:hover": { backgroundColor: palette.indigoSoft, opacity: 0.85 },
            }}
          >
            Decide
          </Button>
        ),
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: `1px solid ${palette.line}`,
        overflow: "hidden",
      }}
    >
      {!loading && rows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 7 }}>
          <InboxRoundedIcon sx={{ fontSize: 42, color: palette.line }} />
          <Typography sx={{ mt: 1, fontSize: 13, color: palette.slate }}>
            {searching ? "No carry-forward sessions match your search." : "No carry-forward sessions."}
          </Typography>
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            fontFamily: "Nunito, sans-serif",
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: palette.bg,
              borderBottom: `1px solid ${palette.line}`,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "12.5px",
              fontWeight: 700,
              color: palette.slate,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "13px",
              borderBottom: `1px solid ${palette.line}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#F5F6F8",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${palette.line}`,
            },
          }}
        />
      )}
    </Paper>
  );
}
